import { DEFAULT_NEW_BUILDING_STOREY_COUNT } from './store/design.store';
import { registerProjectDesignCamera } from './helpers/map-camera-handoff.helpers';
import { reportDesignPersistError } from './helpers/design-snapshot-persist.helpers';
import {
  useProjectOpening,
  notifyProjectOpeningRendered,
} from '@features/projects/project-opening.store';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import type { MapOptions, StyleSpecification } from 'mapbox-gl';
import { makeStyles } from 'tss-react/mui';
import amplitudeLog from '@client/amplitude';
import {
  AMP_DESIGN_MAP_VIEW_MODE_TOGGLE,
  AMP_DESIGN_PDF_UNDERLAY_CALIBRATE,
  AMP_DESIGN_PDF_UNDERLAY_IMPORT,
} from '@shared/constants';
import { GEOMETRY_IMPORT_EXTENSIONS } from '@shared/helpers/design/geometry-import-capabilities.helpers';
import {
  DESIGN_COLLAPSED_TABS_INSET_VAR,
  DESIGN_RIGHT_PANEL_WIDTH_VAR,
  useDesignRightPanelWidth,
} from './design-right-panel-resize.hook';
import { MapCameraCapture } from './components/MapCameraCapture';
import { EmptyStateOverlay } from './components/EmptyStateOverlay';
import { MapTooltipLayer } from './components/MapTooltipLayer';
import { TemplatePlacementWarning } from './components/TemplatePlacementWarning';
import { MapViewSettings } from './components/MapViewSettings';
import { PdfGeometryImportDialog } from './components/PdfGeometryImportDialog';
import { PdfBlueprintUnderlayDialog } from './components/PdfBlueprintUnderlayDialog';
import { BuildingOutOfViewBar } from './components/BuildingOutOfViewBar';
import { BlendDebugPanel } from './components/BlendDebugPanel';
import { MapLocationSearch } from './components/MapLocationSearch';
import { ViewModeToggle, type ViewMode } from './components/ViewModeToggle';
import { useFacadeStoreyHeightTooltips } from './hooks/use-facade-storey-height-tooltips.hook';
import { SelectedBuildingGfaLabel } from './components/SelectedBuildingGfaLabel';
import { shouldRemoveSelectedBlueprintUnderlay } from './helpers/blueprint-underlay-keyboard.helpers';
import { isDesignCanvasTypingTarget } from './helpers/design-canvas-toolbar.helpers';
import { PDF_IMPORT_MAP_LAYER_SETTINGS } from './helpers/design-map-layer-settings.helpers';
import { setNodonDesignMapReady } from '@client/helpers/app-readiness.helpers';
import { useConfig } from '@client/providers/ConfigProvider';
import { useSelectedStoreyShapes } from './hooks/shape.hooks';
import {
  getBuildingStoreys,
  isEmptyStoreysGeometry,
} from '@shared/helpers/design/storey.helpers';
import { getSiteFootprintStoreys } from './helpers/building-viewport.helpers';
import {
  getBuildingDesign,
  persistBlueprintUnderlayById,
  persistBlueprintUnderlays,
  removePersistedBlueprintUnderlayById,
  useActiveBlueprintUnderlay,
  useBlueprintUnderlayBuildingId,
  useBlueprintUnderlays,
  useBuildingDesign,
  useBuildingLocation,
  useUpdateBuildingDesign,
  useUpdateBuildingLocation,
} from './hooks/design.hooks';
import { useBlueprintUnderlaysWithRasters } from './hooks/blueprint-underlay-rasters.hook';
import { selectBlueprintUnderlayIdsToHydrate } from './helpers/blueprint-underlay-raster-hydration.helpers';
import { applyBlueprintOpacityPreview } from './helpers/blueprint-opacity-preview.helpers';
import { getBuildingDisplayName } from '@shared/helpers/design/building-name.helpers';
import {
  useDeselectBuilding,
  useSelectBuilding,
  useStoreys,
} from './hooks/storeys.hooks';
import {
  alignBlueprintUnderlayPose,
  getBuildingBlueprintUnderlays,
  getBuildingFootprintCenter,
  getDefaultBlueprintStoreyAssignment,
  getNormalizedBlueprintUnderlays,
  getStoreyLevel,
  isBlueprintUnderlaySiblingOf,
} from '@shared/helpers/design/blueprint-underlay.helpers';
import {
  getWritableBuildingId,
  useBlueprintImportRequestId,
  useBlueprintImportRequestKind,
  useBuildingCandidate,
  useBlueprintInteractionMode,
  useBlueprintOpacityPreview,
  useBuildingCandidateFootprintTemplate,
  useBuildingCandidateStoreyCount,
  useBuildingCandidateTargetGfa,
  useCalibratePoints,
  useDesignMapTool,
  useDesignViewMode,
  useHasVisibleBuildingSelection,
  useHoveredBuildingId,
  useIsBuildingOutlineSelected,
  useSelectedBuildingId,
  useSelectedStoreyId,
  useQuickTopDownRequestId,
  useFootprintCenterPlacementRequestId,
  useSetBlueprintInteractionMode,
  useSetCalibratePoints,
  useSetDesignMapCamera,
  useSetDesignMapTool,
  useSetDesignViewMode,
  useSetManualBlueprintUnderlayId,
  useShowBackgroundMap,
  useSetShowBlueprintUnderlay,
  useShowBlueprintUnderlay,
  useShowNearbyBuildings,
} from './store/design-store.hooks';
import {
  getBuildingFootprintTemplateGeometry,
  BUILDING_FOOTPRINT_TEMPLATES,
  FOOTPRINT_TEMPLATE_DRAG_TYPE,
} from './helpers/building-footprint-template.helpers';
import { hasSoleEmptyBuilding } from './helpers/building-candidate.helpers';
import { getDesignMapBuildingGeometry } from './helpers/storey-selection.helpers';
import {
  ensureDesignSessionForProject,
  useDesignStore,
  type BlueprintImportRequestKind,
} from './store/design.store';
import { setDesignMapLayerSettings } from './store/design-map-layer-settings.store';
import { useIsReadonly, useUserIsAdmin } from '@client/hooks/use-user.hooks';
import { useUIState } from '@client/store/ui';
import {
  useDesignMapInstance,
  useDesignMapInteractions,
  useDesignMapStateSync,
} from './hooks/design-map.hooks';
import { useProject, useProjectId } from '@client/store/project/project.hook';
import { requestPdfGeometryImport } from './api/design-ai.api';
import { persistGeometryImport } from './helpers/geometry-import-persist.helpers';
import { assertUnreachable } from '@shared/helpers/function.helpers';
import type { GeometryImportRequest } from './helpers/geometry-import-dialog.helpers';
import {
  isPdfDropFile,
  resolveMapDropTarget,
} from './helpers/map-file-drop.helpers';
import { DropZoneOverlay, type CsvDropState } from '@features/csv-import';
import type { DesignMap } from './map';
import { waitForMapIdle } from './helpers/map.helpers';
import type { BlueprintUnderlay } from '@shared/models/design/blueprint-underlay.model';
import { getBlueprintCalibrationUiStatus } from './helpers/blueprint-calibration-status.helpers';
import { Button } from '@ui/buttons';
import { BLUEPRINT_MEASURE_HINT } from './helpers/pdf-blueprint-import.helpers';
import {
  AUTOMATIC_SCALE_ADJUST_LABEL,
  AUTOMATIC_SCALE_SET_MESSAGE,
  resolveBlueprintPostImportAction,
} from './helpers/blueprint-post-import-chrome.helpers';
import { askApplyToAllPages } from './components/ApplyToAllPagesDialog';
import {
  applyBlueprintCalibrationToUnderlays,
  applyBlueprintPrimaryCalibration,
  findBlueprintCalibrationSource,
  type BlueprintCalibrationApplyScope,
} from '@shared/helpers/design/blueprint-calibration.helpers';
import { useErrorHandler } from '@client/hooks/use-error-handler.hooks';
import { useSnackbar } from '@client/hooks/use-snackbar.hooks';
import type { BlueprintCalibrateLabel } from './blueprint-underlay/blueprint-underlay-controller';
import { BlueprintCalibrateLengthPopup } from './components/BlueprintCalibrateLengthPopup';
import {
  tryBuildSilentBlueprintUnderlays,
  type SilentBlueprintPlaceResult,
} from './helpers/blueprint-silent-place.helpers';
import {
  blueprintUnderlayPageMenuId,
  resolveBlueprintContextMenuPageId,
} from './helpers/blueprint-underlay-page-menu.helpers';
import { BlueprintUnderlayPageMenu } from './components/BlueprintUnderlayPageMenu';
import { useBlueprintUnderlayPageActions } from './hooks/use-blueprint-underlay-page-actions.hook';
import { KeyboardKeys } from '@shared/models/keyboard.model';
import { openMenu } from '@client/hooks/use-menu.hooks';

/**
 * Bottom inset used by the readonly variant to clip the mandatory Mapbox logo
 * and attribution chrome out of the visible area.
 */
const READONLY_LOGO_CROP_PX = 50;

const useStyles = makeStyles<{ hideMapboxLogo: boolean }>()(
  (_theme, { hideMapboxLogo }) => ({
    root: {
      display: 'flex',
      width: '100%',
      height: '100%',
      position: 'relative',
      overflow: 'hidden',
      '& .mapboxgl-ctrl-attrib': {
        display: 'none',
      },
      ...(hideMapboxLogo && {
        '& .mapboxgl-ctrl-logo': {
          display: 'none',
        },
      }),
    },
    mapViewportWrapper: {
      position: 'fixed',
      inset: 0,
      zIndex: 0,
      overflow: 'hidden',
    },
    mapInlineWrapper: { width: '100%', height: '100%' },
    viewportLabels: {
      position: 'fixed',
      inset: 0,
      pointerEvents: 'none',
      zIndex: 2,
    },
    map: {
      height: '100%',
      width: '100%',
      outline: 'none',
      '&:focus': {
        outline: 'none',
      },
      '&:focus-visible': {
        outline: 'none',
      },
    },
    // Wrapper used by the readonly variant. We can't put `position: absolute`
    // directly on the mapbox container because `.mapboxgl-map { position:
    // relative }` from mapbox CSS wins and collapses the container to 0 width.
    // Instead we let mapbox keep its own positioning and crop the bottom-left
    // Mapbox logo by extending this wrapper below the visible area (parent has
    // overflow: hidden).
    mapReadonlyWrapper: {
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
    },
  }),
);

/**
 * Camera overrides for the readonly variant. All fields are optional; any
 * missing field falls back to the project map defaults from
 * `mapConfigDefaults` (zoom 18, pitch 60, bearing -17.6). Initial values are
 * applied on attach via {@link buildReadonlyMapOptions}; later prop changes are
 * pushed to the live Mapbox instance with `jumpTo` in {@link Map3d}.
 */
export type Map3dReadonlyCamera = {
  zoom?: number;
  pitch?: number;
  bearing?: number;
};

/**
 * Minimal Mapbox style for the readonly variant. Contains only a transparent
 * background layer so the mapbox canvas blends with whatever is rendered
 * behind it. No tiles or label sources are loaded, which also saves the
 * network cost of fetching the standard basemap.
 *
 * Three.js building/extrusion layers attach as Mapbox custom layers and work
 * with any style as long as it's loaded, so this empty style is sufficient.
 */
const READONLY_TRANSPARENT_STYLE: StyleSpecification = {
  version: 8,
  sources: {},
  layers: [
    {
      id: 'transparent-background',
      type: 'background',
      paint: { 'background-color': 'rgba(0, 0, 0, 0)' },
    },
  ],
};

/**
 * Build the Mapbox options used by the readonly variant.
 *
 * - `interactive: false` disables all pan/zoom/rotate gestures so the surface
 *   is fully frozen (e.g. report cover page).
 * - The transparent style above replaces the standard basemap, so the canvas
 *   shows only the three.js building on top of whatever color is behind it.
 * - Optional camera overrides let consumers tune the framing for narrower
 *   containers (the design tab's default zoom 18 is too tight when the map
 *   is rendered in a smaller report tile).
 */
const buildReadonlyMapOptions = (
  camera: Map3dReadonlyCamera | undefined,
): Omit<MapOptions, 'container' | 'center'> => ({
  interactive: false,
  preserveDrawingBuffer: true,
  style: READONLY_TRANSPARENT_STYLE,
  ...(camera?.zoom !== undefined && { zoom: camera.zoom }),
  ...(camera?.pitch !== undefined && { pitch: camera.pitch }),
  ...(camera?.bearing !== undefined && { bearing: camera.bearing }),
});

/** Mapbox options for the interactive editor map (admin screenshot capture needs a readable buffer). */
const EDITOR_MAP_ATTACH_OPTIONS: Omit<MapOptions, 'container' | 'center'> = {
  preserveDrawingBuffer: true,
};

const isFileDrop = (event: React.DragEvent<HTMLElement>): boolean => {
  return Array.from(event.dataTransfer?.types ?? []).includes('Files');
};

/**
 * Visual/interaction variant of the {@link Map3d} surface.
 *
 * - `'editor'`: full design tab experience with overlays, drafting, drop
 *   handlers, view-mode toggle, screenshot capture and editing wiring.
 * - `'readonly'`: minimal frozen building view for embeds (e.g. report cover).
 *   Uses a transparent Mapbox style, no editor overlays, and all map gestures
 *   disabled after attach. Multiple readonly instances may be active at once.
 */
export type Map3dVariant = 'editor' | 'readonly';

type GeometryImportHandler = (input: GeometryImportRequest) => Promise<void>;

type Map3dProps = {
  /**
   * Determines which UI overlays and editing wiring are mounted on top of the
   * underlying {@link import('./map').DesignMap}. Defaults to `'editor'`.
   */
  variant?: Map3dVariant;
  /**
   * Optional camera overrides (zoom/pitch/bearing) for the readonly variant.
   * The initial values are captured on first mount via
   * {@link buildReadonlyMapOptions}. Later changes are applied to the live
   * Mapbox instance via {@link mapboxgl.Map.jumpTo} so consumers (e.g. the
   * report cover page) can recompute fit-zoom on container resize without
   * tearing down the map. Ignored for the editor variant.
   */
  readonlyCamera?: Map3dReadonlyCamera;
  /**
   * Pixels to extend the readonly map below the clip rect to hide the Mapbox
   * logo (default {@link READONLY_LOGO_CROP_PX}). Use
   * {@link readonlyHideMapboxLogo} on tiny slots instead of shrinking this.
   */
  readonlyLogoCropPx?: number;
  /**
   * Hides the Mapbox logo control in the readonly variant (e.g. report sidebar
   * thumbnail) without extending the map canvas below the clip rect.
   */
  readonlyHideMapboxLogo?: boolean;
  /**
   * Called when the underlying {@link DesignMap} is attached or torn down.
   * Pass a stable callback; it is invoked with `undefined` on unmount.
   */
  onMapReady?: (map: DesignMap | undefined) => void;
  /**
   * Called after the map has attached and reached a real Mapbox idle frame
   * (not a wait timeout). Print/PDF uses this to snapshot the WebGL canvas
   * only after the 3D building has painted — Chrome's print pipeline does
   * not rasterize canvases.
   */
  onIdle?: () => void;
  /**
   * Extra right inset for the north control and search icon, so they sit
   * clear of the collapsed tab menu.
   */
  controlsRightInset?: number;
  /** Paint the editor map behind the app chrome, cropped 40 px at the bottom. */
  fullViewport?: boolean;
};

export const Map3d: React.FC<Map3dProps> = ({
  variant = 'editor',
  readonlyCamera,
  readonlyLogoCropPx = READONLY_LOGO_CROP_PX,
  readonlyHideMapboxLogo = false,
  onMapReady,
  onIdle,
  controlsRightInset = 0,
  fullViewport = false,
}) => {
  const { classes } = useStyles({ hideMapboxLogo: readonlyHideMapboxLogo });
  const [config] = useConfig();
  const project = useProject();
  const shapes = useSelectedStoreyShapes();
  const storeys = useStoreys();
  const deselectBuilding = useDeselectBuilding();
  const selectedStoreyId = useSelectedStoreyId();
  const selectedBuildingId = useSelectedBuildingId();
  const design = useBuildingDesign();
  const hasVisibleBuildingSelection = useHasVisibleBuildingSelection();
  // A brand-new design still needs its sole empty building as a draft target.
  // Completed designs do not get an implicit visible selection on reload.
  const hasBuildingSelection =
    hasVisibleBuildingSelection ||
    (selectedBuildingId === undefined && hasSoleEmptyBuilding(design));
  const mapGeometry = useMemo(
    () =>
      getDesignMapBuildingGeometry(
        design,
        selectedBuildingId,
        hasBuildingSelection,
      ),
    [design, selectedBuildingId, hasBuildingSelection],
  );
  const otherBuildings = mapGeometry.otherBuildings;
  const footprintStoreys = useMemo(
    () => getSiteFootprintStoreys(mapGeometry.storeys, otherBuildings),
    [mapGeometry.storeys, otherBuildings],
  );
  const hoveredBuildingId = useHoveredBuildingId();
  const isBuildingOutlineSelected = useIsBuildingOutlineSelected();
  const hoveredBuilding = useMemo(
    () => otherBuildings.find((building) => building.id === hoveredBuildingId),
    [hoveredBuildingId, otherBuildings],
  );
  const location = useBuildingLocation();
  const userIsReadonly = useIsReadonly();
  const isUserAdmin = useUserIsAdmin();

  const isReadonlyVariant = variant === 'readonly';
  const rightPanelWidth = useDesignRightPanelWidth();
  // readonly variant always behaves as readonly regardless of user role
  const mapReadonly = isReadonlyVariant || userIsReadonly;
  // hide admin-only affordances (drop zone, debug panel, render capture)
  const showAdminAffordances = !isReadonlyVariant && isUserAdmin;
  // Editors can drop a PDF plan as an underlay; AI geometry import stays admin-only.
  const allowBlueprintDrop = !isReadonlyVariant && !userIsReadonly;
  // selection rendering is suppressed in readonly to avoid the blue highlight
  const syncedSelectedStoreyId = isReadonlyVariant
    ? undefined
    : selectedStoreyId;

  const updateBuildingDesign = useUpdateBuildingDesign();
  const updateBuildingLocation = useUpdateBuildingLocation();
  const errorHandler = useErrorHandler();
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const blueprintUnderlays = useBlueprintUnderlays();
  const blueprintUnderlayBuildingId = useBlueprintUnderlayBuildingId();
  const selectBuilding = useSelectBuilding();
  const pdfImportBuildings = useMemo(() => {
    const pages = getNormalizedBlueprintUnderlays(design);
    return design.buildings.map((building, index) => {
      const own = getBuildingBlueprintUnderlays(pages, building.id);
      return {
        id: building.id,
        name: getBuildingDisplayName(building, index),
        storeys: building.storeys,
        matchScaleFrom: findBlueprintCalibrationSource(own) ?? own[0],
      };
    });
  }, [design]);
  const blueprintUnderlay = useActiveBlueprintUnderlay();
  const { removePage } = useBlueprintUnderlayPageActions();
  const blueprintRasterHydrateIds = useMemo(() => {
    // Report cover mounts this map readonly and never draws the sheet.
    if (isReadonlyVariant) return [];
    return selectBlueprintUnderlayIdsToHydrate({
      underlayIds: blueprintUnderlays.map((page) => page.id),
      activeUnderlayId: blueprintUnderlay?.id,
    });
  }, [blueprintUnderlay?.id, blueprintUnderlays, isReadonlyVariant]);
  const underlaysWithRasters = useBlueprintUnderlaysWithRasters(
    blueprintUnderlays,
    blueprintRasterHydrateIds,
  );
  const blueprintUnderlayWithRaster = useMemo(() => {
    if (!blueprintUnderlay) return undefined;
    return (
      underlaysWithRasters.find((page) => page.id === blueprintUnderlay.id) ??
      blueprintUnderlay
    );
  }, [blueprintUnderlay, underlaysWithRasters]);
  const blueprintOpacityPreview = useBlueprintOpacityPreview();
  const blueprintUnderlayForMap = useMemo(
    () =>
      applyBlueprintOpacityPreview(
        blueprintUnderlayWithRaster,
        blueprintOpacityPreview,
      ),
    [blueprintOpacityPreview, blueprintUnderlayWithRaster],
  );
  const setManualBlueprintUnderlayId = useSetManualBlueprintUnderlayId();
  const projectId = useProjectId();
  const { map, containerRef, openingFromProjectList } = useDesignMapInstance({
    accessToken: config?.mapboxAccessToken,
    location,
    attachOptions: isReadonlyVariant
      ? buildReadonlyMapOptions(readonlyCamera)
      : EDITOR_MAP_ATTACH_OPTIONS,
    // Remember zoom/pitch/bearing/center across Design ↔ Build remounts.
    sessionCameraProjectId: isReadonlyVariant ? undefined : projectId,
  });

  const blueprintMode = useBlueprintInteractionMode();
  const setBlueprintMode = useSetBlueprintInteractionMode();
  const designMapTool = useDesignMapTool();
  const setDesignMapTool = useSetDesignMapTool();
  const calibratePoints = useCalibratePoints();
  const setCalibratePoints = useSetCalibratePoints();
  const [calibrateLabels, setCalibrateLabels] = useState<
    BlueprintCalibrateLabel[]
  >([]);
  const [underlayDialogOpen, setUnderlayDialogOpen] = useState(false);
  const [underlayFile, setUnderlayFile] = useState<File | undefined>(undefined);
  const lastUnderlayFileRef = useRef<File | undefined>(undefined);
  /**
   * An import placed unscaled sheets — measure once the dialog is gone.
   * False when scale was inherited or the dialog was cancelled.
   */
  const pendingSetScaleRef = useRef(false);
  /** Show Automatic scale set / Adjust after the dialog exit fade. */
  const pendingInheritedScaleToastRef = useRef(false);
  /** When the PDF picker is opened for edit-pages (no cached file). */
  const pendingPickerKindRef = useRef<BlueprintImportRequestKind | undefined>(
    undefined,
  );
  /** Sheet to fit after the import dialog closes (full canvas size). */
  const pendingFlyToUnderlayRef = useRef<BlueprintUnderlay | undefined>(
    undefined,
  );
  /**
   * Map chrome (2D / hide basemap / underlay focus) deferred until the import
   * dialog finishes fading — applying it while open shifts the modal.
   */
  const pendingImportMapChromeRef = useRef<BlueprintUnderlay | undefined>(
    undefined,
  );
  const blueprintPdfInputRef = useRef<HTMLInputElement>(null);
  const geometryImportInputRef = useRef<HTMLInputElement>(null);
  const blueprintImportRequestId = useBlueprintImportRequestId();
  const blueprintImportRequestKind = useBlueprintImportRequestKind();
  const footprintCenterPlacementRequestId =
    useFootprintCenterPlacementRequestId();
  const lastHandledFootprintPlacement = useRef(
    footprintCenterPlacementRequestId,
  );
  const quickTopDownRequestId = useQuickTopDownRequestId();
  const templatePreview$ = useMemo(
    () => map?.getInteractionStreams()?.draftPreview$,
    [map],
  );
  const hasBuildingCandidate = Boolean(useBuildingCandidate());
  const buildingFootprintTemplateId = useBuildingCandidateFootprintTemplate();
  const targetGfa = useBuildingCandidateTargetGfa();
  const storeyCount =
    useBuildingCandidateStoreyCount() ?? DEFAULT_NEW_BUILDING_STOREY_COUNT;
  const buildingFootprintTemplate = useMemo(
    () =>
      getBuildingFootprintTemplateGeometry(buildingFootprintTemplateId, {
        targetGfa,
        storeyCount,
      }),
    [buildingFootprintTemplateId, targetGfa, storeyCount],
  );
  /** `null` until first observe — avoids replaying a stale request after remount. */
  const lastHandledImportRequestId = useRef<number | null>(null);
  const lastHandledQuickTopDownRequestId = useRef(quickTopDownRequestId);

  // Apply readonly camera changes to the live Mapbox instance after mount.
  // `useDesignMapInstance` captures `attachOptions` in a ref so later
  // `readonlyCamera` prop changes are not picked up by the attach phase —
  // this effect bridges that gap (e.g. so the report cover map re-fits when
  // the cqw-driven tile size changes on window resize).
  useEffect(() => {
    if (!isReadonlyVariant || !readonlyCamera) return;
    const mapboxInstance = map?.map;
    if (!mapboxInstance) return;
    mapboxInstance.jumpTo({
      ...(readonlyCamera.zoom !== undefined && { zoom: readonlyCamera.zoom }),
      ...(readonlyCamera.pitch !== undefined && {
        pitch: readonlyCamera.pitch,
      }),
      ...(readonlyCamera.bearing !== undefined && {
        bearing: readonlyCamera.bearing,
      }),
    });
  }, [isReadonlyVariant, map, readonlyCamera]);

  useEffect(() => {
    onMapReady?.(map);
    return () => onMapReady?.(undefined);
  }, [map, onMapReady]);

  // Draft clicks are only subscribed after Mapbox attach; overlay copy can show earlier.
  useEffect(() => {
    const container = containerRef.current;
    setNodonDesignMapReady(container, Boolean(map));
    return () => setNodonDesignMapReady(container, false);
  }, [containerRef, map]);

  const isSelectedBuildingEmpty =
    hasBuildingSelection && isEmptyStoreysGeometry(storeys);
  const [isDrafting, setIsDrafting] = useState(false);

  // Drafting is editor-only; skip subscription for readonly variant.
  useEffect(() => {
    if (isReadonlyVariant) return;
    const draftShape$ = map?.state?.draftShape$;
    if (!draftShape$) return;
    const subscription = draftShape$.subscribe((draft) =>
      setIsDrafting(draft !== undefined),
    );
    return () => subscription.unsubscribe();
  }, [map, isReadonlyVariant]);

  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [importFile, setImportFile] = useState<File | undefined>(undefined);
  const [isDraggingFileOverMap, setIsDraggingFileOverMap] = useState(false);

  // Drop leaked session and import UI when the active project changes.
  useEffect(() => {
    ensureDesignSessionForProject(projectId);
    lastHandledImportRequestId.current = null;
    lastHandledQuickTopDownRequestId.current =
      useDesignStore.getState().quickTopDownRequestId;
    lastHandledFootprintPlacement.current =
      useDesignStore.getState().footprintCenterPlacementRequestId;
    setImportDialogOpen(false);
    setImportFile(undefined);
  }, [projectId]);

  useEffect(() => {
    if (!map || isReadonlyVariant) return;
    if (quickTopDownRequestId === lastHandledQuickTopDownRequestId.current) {
      return;
    }
    lastHandledQuickTopDownRequestId.current = quickTopDownRequestId;
    map.enterQuickTopDown(rightPanelWidth);
  }, [isReadonlyVariant, map, quickTopDownRequestId, rightPanelWidth]);

  const designDropState: CsvDropState = isDraggingFileOverMap
    ? { kind: 'dragging', validity: 'valid' }
    : { kind: 'idle' };
  const {
    showRenderDebug,
    setShowRenderDebug,
    designImportRequest,
    setDesignImportRequest,
  } = useUIState(
    'showRenderDebug',
    'setShowRenderDebug',
    'designImportRequest',
    'setDesignImportRequest',
  );

  const mode = useDesignViewMode();
  const facadeStoreyHeightTooltips = useFacadeStoreyHeightTooltips(
    map,
    location,
    storeys,
    isBuildingOutlineSelected ? undefined : selectedStoreyId,
  );
  const [mapBearing, setMapBearing] = useState(0);
  const setMode = useSetDesignViewMode();
  const setCamera = useSetDesignMapCamera();
  const showBackgroundMap = useShowBackgroundMap();
  const showNearbyBuildings = useShowNearbyBuildings();
  const showBlueprintUnderlay = useShowBlueprintUnderlay();
  const setShowBlueprintUnderlay = useSetShowBlueprintUnderlay();

  // Keep the north compass on the view toggle in sync while the map rotates.
  useEffect(() => {
    if (!map) return;
    map.setBearingChangeHandler(setMapBearing);
    return () => map.setBearingChangeHandler(undefined);
  }, [map]);

  // Persist the full camera, including panel-aware projection padding.
  useEffect(() => {
    if (isReadonlyVariant || !map) return;
    map.setCameraChangeHandler((camera) => {
      setCamera(camera);
      setMode(camera.pitch === 0 ? '2d' : '3d');
    });
    return () => {
      const camera = map.getCameraState();
      if (camera) {
        setCamera(camera);
        setMode(camera.pitch === 0 ? '2d' : '3d');
      }
      map.setCameraChangeHandler(undefined);
    };
  }, [isReadonlyVariant, map, setCamera, setMode]);

  const handleSwitchView = useCallback(
    (newMode: ViewMode) => {
      setMode(newMode);
      map?.setPerspectiveMode(newMode);
      amplitudeLog(AMP_DESIGN_MAP_VIEW_MODE_TOGGLE, { mode: newMode });
    },
    [map, setMode],
  );

  // Seed 2D/3D intent (soft-grey guides) from the store without re-animating.
  useEffect(() => {
    if (!map || isReadonlyVariant) return;
    map.setPerspectiveModeIntent(mode);
  }, [isReadonlyVariant, map, mode]);

  /**
   * Select the active PDF underlay (or deselect it when already selected).
   * Uncalibrated sheets open measure mode, same as import wizard step 3.
   */
  const toggleBlueprintSelection = useCallback(() => {
    if (blueprintMode === 'transform' || blueprintMode === 'calibrate') {
      setCalibratePoints([]);
      setCalibrateLabels([]);
      setBlueprintMode('locked');
      return;
    }
    if (!blueprintUnderlay) return;
    setDesignMapTool('build');
    setCalibratePoints([]);
    setCalibrateLabels([]);
    const needsScale =
      getBlueprintCalibrationUiStatus(blueprintUnderlay?.calibration) ===
      'incomplete';
    setBlueprintMode(needsScale ? 'calibrate' : 'transform');
  }, [
    blueprintMode,
    blueprintUnderlay,
    setBlueprintMode,
    setCalibratePoints,
    setDesignMapTool,
  ]);

  /**
   * Enter on-map measure mode — the single way to set an underlay scale.
   * Anything that wants it (panel kebab, unscaled-import toast) only has to
   * switch the interaction mode; the effect below owns the rest.
   */
  const startCanvasCalibrate = useCallback(() => {
    setDesignMapTool('build');
    setCalibratePoints([]);
    setCalibrateLabels([]);
    setBlueprintMode('calibrate');
  }, [setBlueprintMode, setCalibratePoints, setDesignMapTool]);

  /**
   * Finish the underlay session after scale is set — same as Done: deselect,
   * lock the sheet, fit it, and restore the default Build tool. Empty maps
   * still start a footprint from Build; an existing storey is not forced into Draw.
   */
  const markBlueprintUnderlayDone = useCallback(
    (focus?: BlueprintUnderlay) => {
      setCalibratePoints([]);
      setCalibrateLabels([]);
      setManualBlueprintUnderlayId(undefined);
      setBlueprintMode('locked');
      setDesignMapTool('build');
      if (!focus || !map) return;
      requestAnimationFrame(() => {
        map.flyToBlueprintUnderlay(focus);
      });
    },
    [
      map,
      setBlueprintMode,
      setCalibratePoints,
      setDesignMapTool,
      setManualBlueprintUnderlayId,
    ],
  );

  /**
   * Toast when a new PDF inherited scale from an earlier sheet.
   */
  const showAutomaticScaleToast = useCallback(() => {
    enqueueSnackbar(AUTOMATIC_SCALE_SET_MESSAGE, {
      variant: 'success',
      autoHideDuration: 8000,
      action: (snackbarId) => (
        <Button
          variant="text"
          size="small"
          onClick={() => {
            closeSnackbar(snackbarId);
            startCanvasCalibrate();
          }}
        >
          {AUTOMATIC_SCALE_ADJUST_LABEL}
        </Button>
      ),
    });
  }, [closeSnackbar, enqueueSnackbar, startCanvasCalibrate]);

  /**
   * Measure-mode chrome. Forces 2D: a pitched view picks the ground plane, so
   * points measured on an elevated sheet would land off the drawing.
   */
  const measureModeActiveRef = useRef(false);
  useEffect(() => {
    const sheet = blueprintUnderlay;
    // No sheet on this storey means nothing to measure — isolating the map and
    // prompting for two points would leave the user in an empty view.
    const active = blueprintMode === 'calibrate' && sheet !== undefined;
    if (active === measureModeActiveRef.current) return;
    measureModeActiveRef.current = active;
    if (!active || !sheet) return;

    handleSwitchView('2d');
    // Clicks are ignored while the sheet is hidden — bring it back first.
    if (!showBlueprintUnderlay) setShowBlueprintUnderlay(true);
    if (!sheet.visible) {
      void persistBlueprintUnderlayById(
        { ...sheet, visible: true },
        updateBuildingDesign,
      ).catch((error: unknown) => {
        reportDesignPersistError(error, errorHandler);
      });
    }
    // Fit the sheet with margin so both measure points are reachable.
    if (map) {
      requestAnimationFrame(() => {
        map.flyToBlueprintUnderlay(sheet);
      });
    }
    enqueueSnackbar(BLUEPRINT_MEASURE_HINT, {
      variant: 'default',
      autoHideDuration: 8000,
    });
  }, [
    blueprintMode,
    blueprintUnderlay,
    enqueueSnackbar,
    errorHandler,
    handleSwitchView,
    map,
    setShowBlueprintUnderlay,
    showBlueprintUnderlay,
    updateBuildingDesign,
  ]);

  const handleDragOver = useCallback(
    (e: React.DragEvent<HTMLElement>) => {
      if (
        hasBuildingCandidate &&
        e.dataTransfer.types.includes(FOOTPRINT_TEMPLATE_DRAG_TYPE)
      ) {
        e.preventDefault();
        e.dataTransfer.dropEffect = 'copy';
        return;
      }
      if (!allowBlueprintDrop && !showAdminAffordances) return;
      if (!isFileDrop(e)) return;
      e.preventDefault();
      e.dataTransfer.dropEffect = 'copy';
    },
    [allowBlueprintDrop, showAdminAffordances, hasBuildingCandidate],
  );

  const handleDragEnter = useCallback(
    (e: React.DragEvent<HTMLElement>) => {
      if (!allowBlueprintDrop && !showAdminAffordances) return;
      if (!isFileDrop(e)) return;
      e.preventDefault();
      setIsDraggingFileOverMap(true);
    },
    [allowBlueprintDrop, showAdminAffordances],
  );

  const handleDragLeave = useCallback(
    (e: React.DragEvent<HTMLElement>) => {
      if (!allowBlueprintDrop && !showAdminAffordances) return;
      if (!isFileDrop(e)) return;
      // Drag events fire when moving between children; only clear when actually leaving the container.
      const related = e.relatedTarget;
      if (related instanceof Node && e.currentTarget.contains(related)) return;
      setIsDraggingFileOverMap(false);
    },
    [allowBlueprintDrop, showAdminAffordances],
  );

  const handleImportDialogClose = useCallback(() => {
    setImportDialogOpen(false);
    setImportFile(undefined);
  }, []);

  const handleUnderlayDialogClose = useCallback(() => {
    setUnderlayDialogOpen(false);
  }, []);

  /**
   * Apply post-import map chrome (locked 2D, no basemap, focused sheet).
   * Camera fit is owned by {@link DesignMap.flyToBlueprintUnderlay} so we do not
   * start a competing pitch animation here.
   */
  const applyImportMapChrome = useCallback(
    (focus: BlueprintUnderlay) => {
      setCalibratePoints([]);
      setMode('2d');
      map?.setPerspectiveMode('2d');
      setDesignMapLayerSettings(PDF_IMPORT_MAP_LAYER_SETTINGS);
      map?.setShowBackgroundMap(false);
      map?.setShowNearbyBuildings(false);
      map?.setBlueprintUnderlay({ ...focus, visible: true });
    },
    [map, setCalibratePoints, setMode],
  );

  /**
   * After the import dialog fade-out: map chrome, drop file handle, then set
   * the scale (placed) or just fit the sheet (cancelled with pages already on
   * the map).
   */
  const handleUnderlayDialogExited = useCallback(() => {
    const chromeFocus = pendingImportMapChromeRef.current;
    pendingImportMapChromeRef.current = undefined;
    if (chromeFocus) applyImportMapChrome(chromeFocus);

    setUnderlayFile(undefined);
    const focus = pendingFlyToUnderlayRef.current;
    pendingFlyToUnderlayRef.current = undefined;
    const measure = pendingSetScaleRef.current;
    pendingSetScaleRef.current = false;
    const inheritedToast = pendingInheritedScaleToastRef.current;
    pendingInheritedScaleToastRef.current = false;
    if (measure) {
      // Measure mode does its own 2D + fit — no second fly here.
      startCanvasCalibrate();
      return;
    }
    if (inheritedToast) {
      markBlueprintUnderlayDone(focus);
      showAutomaticScaleToast();
      return;
    }
    if (!focus || !map) return;
    // Fit after chrome so the underlay is on the map; rAF lets Mapbox apply
    // style/layout from the 2D UI toggle before computing bounds.
    requestAnimationFrame(() => {
      map.flyToBlueprintUnderlay(focus);
    });
  }, [
    applyImportMapChrome,
    map,
    markBlueprintUnderlayDone,
    showAutomaticScaleToast,
    startCanvasCalibrate,
  ]);

  const persistBlueprintUnderlay = useCallback(
    async (underlay: BlueprintUnderlay | undefined) => {
      try {
        if (!underlay) {
          const activeId = blueprintUnderlay?.id;
          if (!activeId) return;
          await removePersistedBlueprintUnderlayById(
            activeId,
            updateBuildingDesign,
          );
          return;
        }
        await persistBlueprintUnderlayById(underlay, updateBuildingDesign);
      } catch (error: unknown) {
        reportDesignPersistError(error, errorHandler);
      }
    },
    [blueprintUnderlay?.id, errorHandler, updateBuildingDesign],
  );

  /**
   * Persist a calibrated sheet using the same multi-page scope rules as step 3.
   * With multiple pages the scope question comes first — cancelling leaves the
   * scale unset. Returns whether the calibration was applied.
   */
  const persistCalibrationResult = useCallback(
    async (calibrated: BlueprintUnderlay) => {
      const siblings = getNormalizedBlueprintUnderlays(
        getBuildingDesign(),
      ).filter((page) =>
        isBlueprintUnderlaySiblingOf(
          page,
          calibrated,
          blueprintUnderlayBuildingId,
        ),
      );

      let scope: BlueprintCalibrationApplyScope = 'active';
      if (siblings.length > 1) {
        const choice = await askApplyToAllPages();
        if (choice === 'cancel') return false;
        scope = choice === 'all' ? 'all' : 'active';
      }

      try {
        await persistBlueprintUnderlays(
          (live) =>
            applyBlueprintCalibrationToUnderlays(
              live,
              calibrated,
              scope,
              blueprintUnderlayBuildingId,
            ),
          updateBuildingDesign,
        );
      } catch (error: unknown) {
        reportDesignPersistError(error, errorHandler);
        return false;
      }
      amplitudeLog(AMP_DESIGN_PDF_UNDERLAY_CALIBRATE, {
        UnderlayId: calibrated.id,
        Scope: scope,
        ProjectId: projectId,
      });
      enqueueSnackbar(
        scope === 'all' ? 'Scale set on all pages' : 'Scale set',
        { variant: 'success' },
      );
      return true;
    },
    [
      blueprintUnderlayBuildingId,
      enqueueSnackbar,
      errorHandler,
      projectId,
      updateBuildingDesign,
    ],
  );

  /**
   * Apply a span measured on the Design canvas (local meters).
   */
  const handleCanvasCalibrationSubmit = useCallback(
    async (meters: number) => {
      if (!blueprintUnderlay) return;
      const points = useDesignStore.getState().calibratePoints;
      const start = points[0];
      const end = points[1];
      if (!start || !end) return;
      const calibrated = applyBlueprintPrimaryCalibration(
        blueprintUnderlay,
        start,
        end,
        meters,
      );
      if (!calibrated) return;
      const applied = await persistCalibrationResult(calibrated);
      // Cancelling clears the drawn measure but stays in calibrate mode.
      setCalibratePoints([]);
      setCalibrateLabels([]);
      if (!applied) return;
      markBlueprintUnderlayDone(calibrated);
    },
    [
      blueprintUnderlay,
      markBlueprintUnderlayDone,
      persistCalibrationResult,
      setCalibratePoints,
    ],
  );

  const handlePlaceUnderlays = useCallback(
    async (underlays: BlueprintUnderlay[]) => {
      if (!underlays.length) return;
      const design = getBuildingDesign();
      // The import dialog may put the pages on another building than the one shown.
      const targetBuildingId =
        underlays[0]?.buildingId ?? blueprintUnderlayBuildingId;
      const existing = getBuildingBlueprintUnderlays(
        getNormalizedBlueprintUnderlays(design),
        targetBuildingId,
      );
      const selectedLevel = getStoreyLevel(
        storeys,
        selectedStoreyId ?? syncedSelectedStoreyId,
      );
      // Drop new pages on the building's active sheet frame so same-size plans
      // stack aligned.
      // The site plan in view is not the building's sheet frame.
      const poseSource =
        targetBuildingId === blueprintUnderlayBuildingId &&
        blueprintUnderlay &&
        !blueprintUnderlay.site
          ? blueprintUnderlay
          : existing[0];
      // With nothing to line up with, start on the building instead of the origin.
      const footprintCenter = poseSource
        ? undefined
        : getBuildingFootprintCenter(design, targetBuildingId);
      const placed = underlays.map((underlay, index) => {
        const posed = poseSource
          ? alignBlueprintUnderlayPose(underlay, poseSource)
          : footprintCenter
            ? { ...underlay, center: footprintCenter }
            : underlay;
        return {
          ...posed,
          buildingId: posed.buildingId ?? targetBuildingId,
          storeyAssignment:
            posed.storeyAssignment ??
            getDefaultBlueprintStoreyAssignment({
              underlays: [...existing, ...underlays.slice(0, index)],
              selectedStoreyLevel: selectedLevel,
              storeys,
            }),
        };
      });
      const focus = placed[0];
      if (!focus) return;

      // Metadata → PUT JPEG → snapshot; keep rasters off the project document.
      await persistBlueprintUnderlays(
        (live) => [...live, ...placed],
        updateBuildingDesign,
      );
      amplitudeLog(AMP_DESIGN_PDF_UNDERLAY_IMPORT, {
        PageCount: placed.length,
        ProjectId: projectId,
      });
      // Show the building the pages were placed on; its storey change clears
      // the manual page, so select it first. With no building selected the
      // site plan is in view, even when the target is the first building.
      if (
        targetBuildingId &&
        (selectedBuildingId === undefined ||
          targetBuildingId !== blueprintUnderlayBuildingId)
      ) {
        selectBuilding(targetBuildingId);
      }
      // Manual override after persist so resolveActive can find the id in the list.
      setManualBlueprintUnderlayId(focus.id);
      const postImport = resolveBlueprintPostImportAction(placed);
      const needsMeasure = postImport.kind === 'startCalibrate';
      pendingSetScaleRef.current = needsMeasure;
      pendingInheritedScaleToastRef.current =
        postImport.kind === 'inheritedScale';
      // Camera fit + map chrome run after the dialog exit fade.
      pendingFlyToUnderlayRef.current = focus;
      if (underlayFile) {
        pendingImportMapChromeRef.current = focus;
      } else {
        // Placed without the wizard, so no dialog exit follows.
        pendingFlyToUnderlayRef.current = undefined;
        pendingSetScaleRef.current = false;
        pendingInheritedScaleToastRef.current = false;
        applyImportMapChrome(focus);
        if (needsMeasure) {
          startCanvasCalibrate();
          return;
        }
        markBlueprintUnderlayDone(focus);
        showAutomaticScaleToast();
      }
    },
    [
      applyImportMapChrome,
      blueprintUnderlay,
      blueprintUnderlayBuildingId,
      markBlueprintUnderlayDone,
      projectId,
      selectBuilding,
      selectedBuildingId,
      selectedStoreyId,
      setManualBlueprintUnderlayId,
      showAutomaticScaleToast,
      startCanvasCalibrate,
      storeys,
      syncedSelectedStoreyId,
      underlayFile,
      updateBuildingDesign,
    ],
  );

  /**
   * Place a single-page PDF without the wizard. Multi-page files open Select
   * pages — the dialog is never used as a loading shell.
   */
  const openBlueprintPdfImport = useCallback(
    async (file: File): Promise<void> => {
      lastUnderlayFileRef.current = file;
      // The building's own pages, never the site plan shown while none is
      // selected.
      const matchScaleFrom =
        pdfImportBuildings.length > 0
          ? pdfImportBuildings[0]?.matchScaleFrom
          : (findBlueprintCalibrationSource(blueprintUnderlays) ??
            blueprintUnderlay ??
            blueprintUnderlays[0]);
      const selectedLevel = getStoreyLevel(
        storeys,
        selectedStoreyId ?? syncedSelectedStoreyId,
      );

      let result: SilentBlueprintPlaceResult | undefined;
      // Several buildings: the dialog asks which one the page is for.
      if (pdfImportBuildings.length > 1) {
        setUnderlayFile(file);
        setUnderlayDialogOpen(true);
        return;
      }
      try {
        result = await tryBuildSilentBlueprintUnderlays({
          file,
          matchScaleFrom,
          selectedStoreyLevel: selectedLevel,
          storeys,
        });
      } catch (error: unknown) {
        reportDesignPersistError(error, errorHandler);
      }

      // Persist a silently built page without the wizard, even if persist fails.
      if (result?.kind === 'placed') {
        try {
          await handlePlaceUnderlays(result.underlays);
        } catch (error: unknown) {
          reportDesignPersistError(error, errorHandler);
        }
        return;
      }

      setUnderlayFile(file);
      setUnderlayDialogOpen(true);
    },
    [
      blueprintUnderlay,
      blueprintUnderlays,
      errorHandler,
      handlePlaceUnderlays,
      pdfImportBuildings,
      selectedStoreyId,
      storeys,
      syncedSelectedStoreyId,
    ],
  );

  const handleDrop = useCallback(
    (e: React.DragEvent<HTMLElement>) => {
      if (e.dataTransfer.types.includes(FOOTPRINT_TEMPLATE_DRAG_TYPE)) {
        e.preventDefault();
        if (!hasBuildingCandidate || !map) return;
        const id = e.dataTransfer.getData(FOOTPRINT_TEMPLATE_DRAG_TYPE);
        const preset = BUILDING_FOOTPRINT_TEMPLATES.find(
          (template) => template.id === id && template.id !== 'custom',
        );
        if (!preset) return;
        const geometry = getBuildingFootprintTemplateGeometry(preset.id, {
          targetGfa,
          storeyCount,
        });
        if (!geometry) return;
        const bounds = e.currentTarget.getBoundingClientRect();
        map.setBuildingFootprintTemplate(geometry);
        map.placeBuildingFootprintTemplateAtScreenPoint([
          e.clientX - bounds.left,
          e.clientY - bounds.top,
        ]);
        return;
      }
      if (!allowBlueprintDrop && !showAdminAffordances) return;
      if (!isFileDrop(e)) return;
      e.preventDefault();
      setIsDraggingFileOverMap(false);

      const target = resolveMapDropTarget(
        Array.from(e.dataTransfer.files ?? []),
        { allowGeometryImport: showAdminAffordances },
      );
      if (!target) {
        console.warn('No supported geometry file found in drop payload');
        return;
      }
      switch (target.kind) {
        case 'geometry-import':
          if (!showAdminAffordances) return;
          setImportFile(target.file);
          setImportDialogOpen(true);
          return;
        case 'blueprint-underlay':
          if (!allowBlueprintDrop) return;
          void openBlueprintPdfImport(target.file);
          return;
        default:
          assertUnreachable(target);
      }
    },
    [
      allowBlueprintDrop,
      openBlueprintPdfImport,
      showAdminAffordances,
      hasBuildingCandidate,
      targetGfa,
      storeyCount,
      map,
    ],
  );

  // Panel: edit pages → reopen wizard; add PDF → file picker.
  // Only act on id increments after mount — never replay a leftover request.
  useEffect(() => {
    if (!allowBlueprintDrop) return;
    if (lastHandledImportRequestId.current === null) {
      lastHandledImportRequestId.current = blueprintImportRequestId;
      return;
    }
    if (blueprintImportRequestId === lastHandledImportRequestId.current) return;
    lastHandledImportRequestId.current = blueprintImportRequestId;
    if (blueprintImportRequestId === 0) return;

    if (blueprintImportRequestKind === 'add-pdf') {
      pendingPickerKindRef.current = undefined;
      blueprintPdfInputRef.current?.click();
      return;
    }

    const lastFile = lastUnderlayFileRef.current;
    if (!lastFile) {
      // Same-session file was lost (refresh) — ask for the PDF again.
      pendingPickerKindRef.current = blueprintImportRequestKind;
      blueprintPdfInputRef.current?.click();
      return;
    }
    void openBlueprintPdfImport(lastFile);
  }, [
    allowBlueprintDrop,
    blueprintImportRequestId,
    blueprintImportRequestKind,
    openBlueprintPdfImport,
  ]);

  // Admin Area: open the PDF underlay picker or the AI geometry picker.
  useEffect(() => {
    if (designImportRequest === 'none') return;
    if (designImportRequest === 'pdf') {
      if (!allowBlueprintDrop) return;
      pendingPickerKindRef.current = undefined;
      setDesignImportRequest('none');
      blueprintPdfInputRef.current?.click();
      return;
    }
    if (!showAdminAffordances) return;
    setDesignImportRequest('none');
    geometryImportInputRef.current?.click();
  }, [
    allowBlueprintDrop,
    designImportRequest,
    setDesignImportRequest,
    showAdminAffordances,
  ]);

  const handleGeometryImport = useCallback<GeometryImportHandler>(
    async (input) => {
      const initiatingProjectId = projectId;
      const response = await requestPdfGeometryImport(input);
      await persistGeometryImport(response, {
        projectId: initiatingProjectId,
        getDesign: getBuildingDesign,
        updateDesign: updateBuildingDesign,
        buildingId: getWritableBuildingId(),
      });
    },
    [projectId, updateBuildingDesign],
  );

  // Re-apply Show-menu layer flags whenever the map remounts (Design ↔ Build).
  useEffect(() => {
    if (!map || isReadonlyVariant) return;
    map.setShowBackgroundMap(showBackgroundMap);
    map.setShowNearbyBuildings(showBackgroundMap ? showNearbyBuildings : false);
  }, [isReadonlyVariant, map, showBackgroundMap, showNearbyBuildings]);

  // Keep Mapbox underlay in sync with persisted design state. No active
  // underlay means the selected storey has none of its own (e.g. a storey that
  // was just added), so the map must not keep another storey's sheet on screen.
  useEffect(() => {
    if (!map || isReadonlyVariant) return;
    if (!blueprintUnderlayForMap) {
      map.setBlueprintUnderlay(undefined);
      return;
    }
    map.setBlueprintUnderlay({
      ...blueprintUnderlayForMap,
      visible: blueprintUnderlayForMap.visible && showBlueprintUnderlay,
    });
  }, [blueprintUnderlayForMap, isReadonlyVariant, map, showBlueprintUnderlay]);

  // Sync canvas tool + PDF selection (transform / calibrate) to the map.
  useEffect(() => {
    if (!map || isReadonlyVariant) return;
    map.setDesignMapTool(designMapTool);
  }, [designMapTool, isReadonlyVariant, map]);

  useEffect(() => {
    if (!map || isReadonlyVariant) return;
    map.setBuildingFootprintTemplate(
      buildingFootprintTemplate,
      hasBuildingCandidate && !buildingFootprintTemplate,
    );
    return () => map.setBuildingFootprintTemplate(undefined, false);
  }, [buildingFootprintTemplate, hasBuildingCandidate, isReadonlyVariant, map]);

  useEffect(() => {
    if (
      !map ||
      isReadonlyVariant ||
      !hasBuildingCandidate ||
      !buildingFootprintTemplate
    )
      return;
    if (
      lastHandledFootprintPlacement.current ===
      footprintCenterPlacementRequestId
    )
      return;
    lastHandledFootprintPlacement.current = footprintCenterPlacementRequestId;
    map.placeBuildingFootprintTemplateAtViewportCenter(rightPanelWidth);
  }, [
    map,
    isReadonlyVariant,
    hasBuildingCandidate,
    buildingFootprintTemplate,
    footprintCenterPlacementRequestId,
    rightPanelWidth,
  ]);

  // Shift+O keeps the open panel width as camera padding so Mapbox's
  // projection center stays in the visible canvas. Remove it on collapse.
  useEffect(() => {
    if (!map || isReadonlyVariant || rightPanelWidth !== 0) return;
    map.clearViewportRightInset();
  }, [isReadonlyVariant, map, rightPanelWidth]);

  // Draw / Move shortcuts (toolbar removed; Build remains the default).
  useEffect(() => {
    if (isReadonlyVariant || userIsReadonly) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (isDesignCanvasTypingTarget(event.target)) return;
      // The storey shortcuts run in the capture phase and claim the keys they
      // handle, so `m` toggles either the mezzanine or this tool — never both.
      if (event.defaultPrevented) return;
      if (event.metaKey || event.ctrlKey || event.altKey) return;

      const key = event.key.toLowerCase();
      if (key === 'escape') {
        event.preventDefault();
        if (designMapTool !== 'build') setDesignMapTool('build');
        deselectBuilding();
        return;
      }
      if (event.shiftKey && key === 'o' && !event.repeat) {
        event.preventDefault();
        map?.alignBearingToBuildingAxes(rightPanelWidth);
        return;
      }
      if (event.shiftKey && key === 't' && !event.repeat) {
        event.preventDefault();
        map?.toggleQuickPitch(rightPanelWidth);
        return;
      }
      if (event.shiftKey && key === 'z' && !event.repeat) {
        event.preventDefault();
        map?.zoomToBuildingSelection(rightPanelWidth);
        return;
      }
      if (key === 'd') {
        event.preventDefault();
        setDesignMapTool(designMapTool === 'draw' ? 'build' : 'draw');
        return;
      }
      if (key === 'm') {
        event.preventDefault();
        setDesignMapTool(
          designMapTool === 'moveBuilding' ? 'build' : 'moveBuilding',
        );
        return;
      }
      if (
        (event.key === KeyboardKeys.BACKSPACE ||
          event.key === KeyboardKeys.DELETE) &&
        blueprintUnderlay &&
        shouldRemoveSelectedBlueprintUnderlay({
          mode: blueprintMode,
          hasUnderlay: true,
        })
      ) {
        event.preventDefault();
        void removePage(blueprintUnderlay.id);
      }
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [
    blueprintMode,
    blueprintUnderlay,
    deselectBuilding,
    designMapTool,
    isReadonlyVariant,
    map,
    removePage,
    rightPanelWidth,
    setDesignMapTool,
    userIsReadonly,
  ]);

  useEffect(() => {
    if (!map || isReadonlyVariant) return;
    map.setBlueprintInteractionMode(blueprintMode);
  }, [blueprintMode, isReadonlyVariant, map]);

  // Measuring hides the building + basemap so only the drawing is clickable.
  useEffect(() => {
    if (!map || isReadonlyVariant) return;
    map.setBlueprintMeasureIsolation(blueprintMode === 'calibrate');
    return () => map.setBlueprintMeasureIsolation(false);
  }, [blueprintMode, isReadonlyVariant, map]);

  useEffect(() => {
    if (!map || isReadonlyVariant) return;
    map.setDesignCanvasHandlers({
      onDraftComplete: () => setDesignMapTool('build'),
      onBlueprintSelectRequest: () => {
        setDesignMapTool('build');
        setBlueprintMode('transform');
      },
      onBlueprintDeselectRequest: () => {
        setCalibratePoints([]);
        setCalibrateLabels([]);
        setBlueprintMode('locked');
      },
      onBlueprintContextMenuRequest: (position) => {
        const state = useDesignStore.getState();
        const design = getBuildingDesign();
        const pageId = resolveBlueprintContextMenuPageId({
          mode: state.blueprintInteractionMode,
          underlays: getNormalizedBlueprintUnderlays(design),
          storeys: getBuildingStoreys(design, state.selectedBuildingId),
          selectedStoreyId: state.selectedStoreyId,
          manualUnderlayId: state.manualBlueprintUnderlayId,
        });
        if (!pageId) return;
        openMenu({
          anchorPosition: position,
          id: blueprintUnderlayPageMenuId(pageId),
        });
      },
    });
    map.setBlueprintUnderlayChangeHandler((next) => {
      void persistBlueprintUnderlay(next);
    });
    map.setBlueprintCalibratePointHandler((point) => {
      const prev = useDesignStore.getState().calibratePoints;
      if (prev.length >= 2) return;
      setCalibratePoints([...prev, point]);
    });
    map.setBlueprintCalibrateLabelsHandler(setCalibrateLabels);
    map.setBlueprintEscapeHandler((result) => {
      setCalibratePoints([]);
      setCalibrateLabels([]);
      if (result !== 'exited-mode') return;
      // Leaving calibrate keeps the PDF selected; leaving transform deselects.
      const mode = useDesignStore.getState().blueprintInteractionMode;
      setBlueprintMode(mode === 'calibrate' ? 'transform' : 'locked');
    });
    return () => {
      map.setDesignCanvasHandlers({});
      map.setBlueprintUnderlayChangeHandler(undefined);
      map.setBlueprintCalibratePointHandler(undefined);
      map.setBlueprintCalibrateLabelsHandler(undefined);
      map.setBlueprintEscapeHandler(undefined);
    };
  }, [
    isReadonlyVariant,
    map,
    persistBlueprintUnderlay,
    setBlueprintMode,
    setCalibratePoints,
    setDesignMapTool,
  ]);

  // Keep the map calibrate overlay in sync when React clears / replaces points.
  useEffect(() => {
    if (!map || isReadonlyVariant) return;
    map.setBlueprintCalibratePoints(calibratePoints);
  }, [calibratePoints, isReadonlyVariant, map]);

  // Nothing selected: no draft target and no click-anywhere affordances.
  useEffect(() => {
    if (!map) return;
    map.setHasBuildingSelection(hasBuildingSelection);
  }, [map, hasBuildingSelection]);

  // Whole building selected: blue outline around its volume.
  useEffect(() => {
    if (!map) return;
    map.setBuildingOutlineSelected(isBuildingOutlineSelected);
  }, [map, isBuildingOutlineSelected]);

  useEffect(() => {
    if (!map) return;
    map.setHoveredBuilding(hoveredBuildingId);
  }, [map, hoveredBuildingId]);

  useDesignMapStateSync({
    map,
    location,
    storeys: mapGeometry.storeys,
    selectedStoreyId: syncedSelectedStoreyId,
    selectedBuildingId: mapGeometry.selectedBuildingId,
  });

  // Keep non-selected buildings rendered as static footprints (DEV-2576).
  useEffect(() => {
    if (!map) return;
    map.setOtherBuildings(otherBuildings);
  }, [map, otherBuildings]);

  useDesignMapInteractions({
    map,
    storeys,
    shapes,
    selectedStoreyId,
    readonly: mapReadonly,
  });

  // Double-click is reserved for Design interactions, not map navigation.
  useEffect(() => {
    map?.map?.doubleClickZoom?.disable?.();
  }, [map]);

  useEffect(() => {
    if (!map?.map) return;
    return registerProjectDesignCamera(map.map, isReadonlyVariant, () =>
      map.exitFacadeMode(),
    );
  }, [map, isReadonlyVariant]);

  const openingPhase = useProjectOpening((state) => state.phase);
  useEffect(() => {
    if (openingPhase !== 'rendering' || !map?.map) return;
    const mapbox = map.map;
    const rendered = () => {
      if (mapbox.areTilesLoaded()) {
        mapbox.off('render', rendered);
        notifyProjectOpeningRendered(projectId);
      }
    };
    mapbox.on('render', rendered);
    mapbox.triggerRepaint();
    return () => {
      mapbox.off('render', rendered);
    };
  }, [openingPhase, map, projectId, storeys]);

  const openingPitchAnimated = useRef(false);
  useEffect(() => {
    if (
      openingPhase !== 'idle' ||
      !openingFromProjectList ||
      !map?.map ||
      openingPitchAnimated.current
    )
      return;
    const mapbox = map.map;
    // Attach has loaded the map; state sync above has applied project geometry.
    // Wait for its first rendered frame, not idle: custom layers repaint continuously.
    const animatePitch = () => {
      openingPitchAnimated.current = true;
      setMode('3d');
      mapbox.easeTo({ pitch: 65, bearing: 25, duration: 900 });
    };
    mapbox.once('render', animatePitch);
    mapbox.triggerRepaint();
    return () => {
      mapbox.off('render', animatePitch);
    };
  }, [map, openingFromProjectList, storeys, setMode, openingPhase]);

  useEffect(() => {
    if (!onIdle || !map?.map) return;
    const mapbox = map.map;
    const controller = new AbortController();
    // Re-runs when `map` or `storeys` change so a new instance / later storeys
    // get their own idle wait. Only `'idle'` means the building has painted.
    void waitForMapIdle(mapbox, { signal: controller.signal }).then(
      (reason) => {
        if (reason !== 'idle') return;
        onIdle();
      },
    );
    return () => {
      controller.abort();
    };
  }, [map, storeys, onIdle]);

  // `map.setReadonly(true)` (called from `useDesignMapInteractions`) re-enables
  // dragPan to keep the shared/readonly editor draggable. The readonly variant
  // wants a fully frozen view, so disable every mapbox gesture explicitly
  // after the interactions hook has run.
  useEffect(() => {
    if (!isReadonlyVariant) return;
    if (!map) return;
    const mapbox = map.map;
    if (!mapbox) return;
    mapbox.dragPan?.disable?.();
    mapbox.scrollZoom?.disable?.();
    mapbox.boxZoom?.disable?.();
    mapbox.dragRotate?.disable?.();
    mapbox.keyboard?.disable?.();
    mapbox.doubleClickZoom?.disable?.();
    mapbox.touchZoomRotate?.disable?.();
    mapbox.touchPitch?.disable?.();
  }, [map, isReadonlyVariant]);

  const pendingCalibrateLabel = useMemo(() => {
    if (blueprintMode !== 'calibrate') return undefined;
    if (calibratePoints.length < 2) return undefined;
    return calibrateLabels.find((label) => label.kind === 'pending-input');
  }, [blueprintMode, calibrateLabels, calibratePoints.length]);

  const calibrateSessionKey =
    calibratePoints[0] && calibratePoints[1]
      ? `${calibratePoints[0][0]},${calibratePoints[0][1]}-${calibratePoints[1][0]},${calibratePoints[1][1]}`
      : '';

  return (
    <div
      className={classes.root}
      data-design-map-content={
        fullViewport && !isReadonlyVariant ? '' : undefined
      }
      style={
        isReadonlyVariant
          ? undefined
          : ({
              [DESIGN_RIGHT_PANEL_WIDTH_VAR]: `${rightPanelWidth}px`,
              [DESIGN_COLLAPSED_TABS_INSET_VAR]: `${controlsRightInset}px`,
            } as React.CSSProperties)
      }
    >
      {!isReadonlyVariant && !userIsReadonly && (
        <MapLocationSearch
          map={map}
          accessToken={config?.mapboxAccessToken}
          proximity={location}
          onLocationSelect={updateBuildingLocation}
        />
      )}

      {!isReadonlyVariant && (
        <ViewModeToggle
          mode={mode}
          onSwitchView={handleSwitchView}
          bearing={mapBearing}
        />
      )}

      {!isReadonlyVariant && (
        <BuildingOutOfViewBar
          map={map}
          location={location}
          storeys={footprintStoreys}
        />
      )}

      {showAdminAffordances && (
        <MapCameraCapture
          map={map}
          mode={mode}
          projectId={project.id}
          location={location}
        />
      )}

      {showAdminAffordances && showRenderDebug && (
        <BlendDebugPanel map={map} onClose={() => setShowRenderDebug(false)} />
      )}

      {!isReadonlyVariant && <BlueprintUnderlayPageMenu />}

      {!isReadonlyVariant && (
        <MapViewSettings
          map={map}
          blueprintUnderlay={blueprintUnderlayWithRaster ?? blueprintUnderlay}
          blueprintSelect={
            blueprintUnderlay
              ? {
                  selected:
                    blueprintMode === 'transform' ||
                    blueprintMode === 'calibrate',
                  onToggle: toggleBlueprintSelection,
                }
              : undefined
          }
        />
      )}

      <div
        className={
          fullViewport && !isReadonlyVariant
            ? classes.viewportLabels
            : undefined
        }
      >
        {!isReadonlyVariant && pendingCalibrateLabel && calibrateSessionKey && (
          <BlueprintCalibrateLengthPopup
            screenPoint={pendingCalibrateLabel.screenPoint}
            sessionKey={calibrateSessionKey}
            onSubmit={(meters) => {
              void handleCanvasCalibrationSubmit(meters);
            }}
          />
        )}

        {!isReadonlyVariant && (
          <>
            <MapTooltipLayer
              map={map}
              disabled={userIsReadonly}
              extraTooltips={facadeStoreyHeightTooltips}
            />
            {!userIsReadonly && (
              <TemplatePlacementWarning preview$={templatePreview$} />
            )}
          </>
        )}

        {!isReadonlyVariant && (
          <SelectedBuildingGfaLabel
            map={map}
            location={location}
            storeys={storeys}
            facadeDimensionsVisible={Boolean(selectedBuildingId)}
            visible={
              isBuildingOutlineSelected &&
              hoveredBuildingId !== selectedBuildingId
            }
          />
        )}

        {!isReadonlyVariant && (
          <SelectedBuildingGfaLabel
            map={map}
            location={location}
            storeys={hoveredBuilding?.storeys ?? []}
            facadeDimensionsVisible={Boolean(
              (isBuildingOutlineSelected || !selectedStoreyId) &&
              hoveredBuilding &&
              hoveredBuilding.id !== selectedBuildingId,
            )}
            visible={Boolean(
              hoveredBuilding && hoveredBuilding.id !== selectedBuildingId,
            )}
          />
        )}
      </div>

      {!isReadonlyVariant && (
        <EmptyStateOverlay
          label={
            buildingFootprintTemplate
              ? 'Click to place the footprint'
              : undefined
          }
          rightInset={rightPanelWidth}
          visible={
            !userIsReadonly &&
            isSelectedBuildingEmpty &&
            !isDrafting &&
            blueprintUnderlays.length === 0
          }
        />
      )}

      {isReadonlyVariant ? (
        <div
          className={classes.mapReadonlyWrapper}
          style={{ bottom: -readonlyLogoCropPx }}
        >
          <div ref={containerRef} className={classes.map} />
        </div>
      ) : (
        <div
          className={
            fullViewport ? classes.mapViewportWrapper : classes.mapInlineWrapper
          }
          data-design-map-viewport={fullViewport ? '' : undefined}
        >
          <div
            ref={containerRef}
            style={fullViewport ? { height: 'calc(100% + 40px)' } : undefined}
            className={classes.map}
            onDragEnter={
              allowBlueprintDrop || showAdminAffordances
                ? handleDragEnter
                : undefined
            }
            onDragOver={
              allowBlueprintDrop || showAdminAffordances || hasBuildingCandidate
                ? handleDragOver
                : undefined
            }
            onDragLeave={
              allowBlueprintDrop || showAdminAffordances
                ? handleDragLeave
                : undefined
            }
            onDrop={
              allowBlueprintDrop || showAdminAffordances || hasBuildingCandidate
                ? handleDrop
                : undefined
            }
          />
        </div>
      )}
      {(allowBlueprintDrop || showAdminAffordances) && (
        <DropZoneOverlay
          state={designDropState}
          labels={{
            neutralTitle: showAdminAffordances
              ? 'Drop PDF or geometry file'
              : 'Drop PDF blueprint',
            neutralSubtitle: showAdminAffordances
              ? '.pdf underlay, or .ifc / .ifczip for AI import'
              : '.pdf',
          }}
        />
      )}
      {allowBlueprintDrop && (
        <input
          ref={blueprintPdfInputRef}
          type="file"
          accept="application/pdf,.pdf"
          hidden
          onChange={(event) => {
            const file = event.target.files?.[0];
            event.target.value = '';
            pendingPickerKindRef.current = undefined;
            if (!file || !isPdfDropFile(file)) return;
            void openBlueprintPdfImport(file);
          }}
        />
      )}
      {showAdminAffordances && (
        <input
          ref={geometryImportInputRef}
          type="file"
          accept={GEOMETRY_IMPORT_EXTENSIONS.join(',')}
          hidden
          onChange={(event) => {
            const file = event.target.files?.[0];
            event.target.value = '';
            if (!file) return;
            setImportFile(file);
            setImportDialogOpen(true);
          }}
        />
      )}
      {allowBlueprintDrop && underlayFile && (
        <PdfBlueprintUnderlayDialog
          open={underlayDialogOpen}
          file={underlayFile}
          storeys={storeys}
          buildings={pdfImportBuildings}
          selectedBuildingId={blueprintUnderlayBuildingId}
          hasExistingUnderlays={blueprintUnderlays.length > 0}
          selectedStoreyLevel={getStoreyLevel(
            storeys,
            selectedStoreyId ?? syncedSelectedStoreyId,
          )}
          matchScaleFrom={
            pdfImportBuildings.find(
              ({ id }) => id === blueprintUnderlayBuildingId,
            )?.matchScaleFrom
          }
          onClose={handleUnderlayDialogClose}
          onExited={handleUnderlayDialogExited}
          onPlaceUnderlays={handlePlaceUnderlays}
        />
      )}
      {showAdminAffordances && importDialogOpen && importFile && (
        <PdfGeometryImportDialog
          open={importDialogOpen}
          file={importFile}
          onClose={handleImportDialogClose}
          onImport={handleGeometryImport}
        />
      )}
    </div>
  );
};
